<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import { Search16 } from '@carbon/icons-vue';
import ContactCustomAttributeItem from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributeItem.vue';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'dashboard/components-next/polaris/ui/input-group';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const { uiSettings } = useUISettings();

const searchQuery = ref('');

const contactAttributes = useMapGetter('attributes/getContactAttributes') || [];

const hasContactAttributes = computed(
  () => contactAttributes.value?.length > 0
);

const processContactAttributes = (
  attributes,
  customAttributes,
  filterCondition
) => {
  if (!attributes.length || !customAttributes) {
    return [];
  }

  return attributes.reduce((result, attribute) => {
    const { attributeKey } = attribute;
    const meetsCondition = filterCondition(attributeKey, customAttributes);

    if (meetsCondition) {
      result.push({
        ...attribute,
        value: customAttributes[attributeKey] ?? '',
      });
    }

    return result;
  }, []);
};

const sortAttributesOrder = computed(
  () =>
    uiSettings.value.conversation_elements_order_conversation_contact_panel ??
    []
);

const sortByUISettings = attributes => {
  // Get saved order from UI settings
  // Same as conversation panel contact attribute order
  const order = sortAttributesOrder.value;

  // If no order defined, return original array
  if (!order?.length) return attributes;

  const orderMap = new Map(order.map((key, index) => [key, index]));

  // Sort attributes based on their position in saved order
  return [...attributes].sort((a, b) => {
    // Get positions, use Infinity if not found in order (pushes to end)
    const aPos = orderMap.get(a.attributeKey) ?? Infinity;
    const bPos = orderMap.get(b.attributeKey) ?? Infinity;
    return aPos - bPos;
  });
};

const usedAttributes = computed(() => {
  const attributes = processContactAttributes(
    contactAttributes.value,
    props.selectedContact?.customAttributes,
    (key, custom) => key in custom
  );

  return sortByUISettings(attributes);
});

const unusedAttributes = computed(() => {
  const attributes = processContactAttributes(
    contactAttributes.value,
    props.selectedContact?.customAttributes,
    (key, custom) => !(key in custom)
  );

  return sortByUISettings(attributes);
});

const filteredUnusedAttributes = computed(() => {
  return unusedAttributes.value?.filter(attribute =>
    attribute.attributeDisplayName
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase())
  );
});

const unusedAttributesCount = computed(() => unusedAttributes.value?.length);
const hasNoUnusedAttributes = computed(() => unusedAttributesCount.value === 0);
const hasNoUsedAttributes = computed(() => usedAttributes.value.length === 0);
</script>

<!-- Polaris (fase 5.4): the attributes section of the contact drawer (a FoldingGroup in Details, R2-5-PC-2 = B).
     Same logic as the woot (used first, then the unused ones with a search; the order is the conversation panel's
     saved order); the rows read as the kit's DetailList (label | value, the 3.3 attributes) and the search is the
     kit's InputGroup. The drawer gives the padding. -->
<template>
  <div v-if="hasContactAttributes" class="grid grid-cols-1 gap-4">
    <div v-if="!hasNoUsedAttributes" class="grid grid-cols-1 gap-1">
      <ContactCustomAttributeItem
        v-for="attribute in usedAttributes"
        :key="attribute.id"
        is-editing-view
        :attribute="attribute"
      />
    </div>
    <div v-if="!hasNoUnusedAttributes" class="flex items-center gap-3">
      <div class="h-px flex-1 bg-polaris-border" />
      <span class="type-polaris-caption text-polaris-muted-foreground">{{
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.UNUSED_ATTRIBUTES', {
          count: unusedAttributesCount,
        })
      }}</span>
      <div class="h-px flex-1 bg-polaris-border" />
    </div>
    <div class="grid grid-cols-1 gap-3">
      <InputGroup v-if="!hasNoUnusedAttributes" class="h-8">
        <InputGroupInput
          v-model="searchQuery"
          type="search"
          :aria-label="
            t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.SEARCH_PLACEHOLDER')
          "
          :placeholder="
            t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.SEARCH_PLACEHOLDER')
          "
        />
        <InputGroupAddon><Search16 aria-hidden="true" /></InputGroupAddon>
      </InputGroup>
      <p
        v-if="filteredUnusedAttributes.length === 0 && !hasNoUnusedAttributes"
        class="type-polaris-caption text-polaris-muted-foreground"
      >
        {{ t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.NO_ATTRIBUTES') }}
      </p>
      <div v-if="!hasNoUnusedAttributes" class="grid grid-cols-1 gap-1">
        <ContactCustomAttributeItem
          v-for="attribute in filteredUnusedAttributes"
          :key="attribute.id"
          :attribute="attribute"
        />
      </div>
    </div>
  </div>
  <p v-else class="type-polaris-caption text-polaris-muted-foreground">
    {{ t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.EMPTY_STATE') }}
  </p>
</template>
